// src/redux/slices/countriesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_ROUTES from "../../config/apiRoutes";

// Thunk: Fetch countries (already present)
export const fetchCountries = createAsyncThunk(
  "countries/fetchCountries",
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      const filterPayload = {
        currentPage: params.currentPage || 1,
        limit: params.limit || 100,
        ...params,
      };
      const response = await axios.post(
        API_ROUTES.COUNTRIES.GET_ALL,
        filterPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch countries"
      );
    }
  }
);

// Thunk: Create country
export const createCountry = createAsyncThunk(
  "countries/createCountry",
  async (countryData, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().auth;
      const response = await axios.post(
        API_ROUTES.COUNTRIES.CREATE,
        countryData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data; // assuming the created country is in response.data.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create country"
      );
    }
  }
);

// Thunk: Delete country by ID
export const deleteCountry = createAsyncThunk(
    "countries/deleteCountry",
    async (countryId, { getState, rejectWithValue }) => {
      try {
        const { token } = getState().auth;
        await axios.delete(
          API_ROUTES.COUNTRIES.DELETE(countryId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return countryId;
      } catch (error) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to delete country"
        );
      }
    }
  );
// Thunk: Update country by ID
export const updateCountry = createAsyncThunk(
    "countries/updateCountry",
    async ({ countryId, countryData }, { getState, rejectWithValue }) => {
      try {
        const { token } = getState().auth;
        const response = await axios.put(
          API_ROUTES.COUNTRIES.UPDATE(countryId),
          countryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Assuming API returns the updated country in response.data.data or response.data
        return response.data.data || response.data;
      } catch (error) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to update country"
        );
      }
    }
  );
const countriesSlice = createSlice({
  name: "countries",
  initialState: {
    countries: [],
    loading: false,
    error: null,
  },
  reducers: {
    // additional synchronous reducers if needed
  },
  extraReducers: (builder) => {
    builder
      // fetchCountries cases
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload.data || [];
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createCountry cases
      .addCase(createCountry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCountry.fulfilled, (state, action) => {
        state.loading = false;
        // Use the new country from either action.payload.data or action.payload directly
        const newCountry = action.payload.data || action.payload;
        state.countries.push(newCountry);
      })
      .addCase(createCountry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete country cases
      .addCase(deleteCountry.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCountry.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted country from the list based on its ID
        state.countries = state.countries.filter(
          (country) => country._id !== action.payload
        );
      })
      .addCase(deleteCountry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update country cases
      .addCase(updateCountry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCountry.fulfilled, (state, action) => {
        state.loading = false;
        // Update the country in the state array
        const index = state.countries.findIndex(
          (country) => country._id === action.payload._id
        );
        if (index !== -1) {
          state.countries[index] = action.payload;
        }
      })
      .addCase(updateCountry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
      
  },
});

export default countriesSlice.reducer;
